/**
 * ChatGPT-authored closeout copy (Oct 2026). Do not paraphrase in UI — use via renderCloseoutBundle.
 */
import { resolveEquityV2, type EquityResolveContext } from './equityV2Resolver';
import { resolveRelevanceV2 } from './relevanceV2Resolver';
import type { AuthoredNarrativeBundle } from './authoredNarrativeRender';
import { renderNarrativeTemplate } from './narrativeTemplateEngine';
import type { NarrativeTokenMap } from './narrativeTemplateEngine';

export type CloseoutEquityContext = { chartId: string; viewId: string } & EquityResolveContext;

export type CloseoutCopyBlock = {
  overview: string;
  keyInsightsTemplate: string;
  relevance: string;
  equity: string;
  authoringStatus: string;
};

export const FLEET_CLOSEOUT: Record<string, CloseoutCopyBlock> = {
  by_fuel_ldv: {
    authoringStatus: 'IMPLEMENTED_SOURCE_PARTIAL',
    overview:
      'This view shows the registered light-duty fleet by fuel or propulsion type. It describes the modeled/registered vehicle population assigned to the selected geography, not the share of miles driven by each technology.',
    keyInsightsTemplate:
      'In {latestYear}, {dominantFleetCategory} was the largest light-duty fleet category in {locationName}, representing {dominantShare}% of displayed LDVs. Plug-in vehicles represented {pluginShare}% of the light-duty fleet.{trendSentence}',
    relevance:
      'Light-duty fleet composition helps planners track vehicle-technology change and provides an input to transportation-emissions, charging-infrastructure, and electrification planning.',
    equity:
      'Fleet composition does not show who owns each vehicle, how much each vehicle is driven, or whether households without vehicles have access to clean transportation options.',
  },
  by_fuel_all: {
    authoringStatus: 'IMPLEMENTED_SOURCE_PARTIAL',
    overview:
      'This view shows the displayed registered fleet across vehicle classes by fuel or propulsion type. It describes vehicle population, not vehicle miles traveled or the share of emissions produced by each fuel.',
    keyInsightsTemplate:
      'In {latestYear}, {dominantFleetCategory} was the largest fuel category in the displayed {locationName} fleet, representing {dominantShare}% of vehicles. Plug-in vehicles represented {pluginShare}% of the displayed fleet.{trendSentence}',
    relevance:
      'Viewing fuel type across the broader fleet provides context for how vehicle technology is changing beyond passenger vehicles and where electrification or alternative-fuel strategies may apply.',
    equity:
      'Fleet-wide counts do not show who owns or uses the vehicles, where they travel, or where traffic-related pollution is experienced. Those questions require activity and exposure data.',
  },
  by_class_vehicle_category: {
    authoringStatus: 'IMPLEMENTED_SOURCE_PARTIAL',
    overview:
      'This view shows the registered/model fleet by vehicle category. It describes how the number of vehicles is distributed across the categories used in the CARB fleet data; it does not show how many miles each category travels.',
    keyInsightsTemplate:
      'In {latestYear}, {dominantClassLabel} was the largest displayed vehicle category in {locationName}, representing {dominantClassShare}% of the fleet. {secondaryClassSentence}{classTrendSentence}',
    relevance:
      'Vehicle-category composition helps distinguish the parts of the fleet to which different efficiency, electrification, freight, and emissions strategies apply.',
    equity:
      'Vehicle-category counts do not show traffic exposure, freight activity, ownership, or where higher-emitting vehicles operate. Community exposure requires roadway and activity data in addition to fleet counts.',
  },
  by_class_duty_class: {
    authoringStatus: 'IMPLEMENTED_SOURCE_PARTIAL',
    overview:
      'This view groups the registered/model fleet into light-duty and heavy-duty vehicles. It compares vehicle population by duty class, not the share of VMT or emissions attributable to each class.',
    keyInsightsTemplate:
      'In {latestYear}, light-duty vehicles represented {ldvShare}% of the displayed {locationName} fleet and heavy-duty vehicles {hdvShare}%. {dutyClassTrendSentence}',
    relevance:
      'Separating light- and heavy-duty vehicles helps planners distinguish passenger-vehicle electrification from freight, delivery, bus, and other heavy-duty strategies.',
    equity:
      'A relatively small heavy-duty fleet can still account for substantial roadway activity or localized pollution. Fleet population alone should not be used as a measure of exposure.',
  },
  intensity: {
    authoringStatus: 'READY_METHOD_HOLD',
    overview:
      'This view shows modeled average greenhouse-gas emissions per mile for the light-duty fleet. It reflects the EMFAC-based fleet/activity calculation and is not a direct roadside measurement or a measure of total transportation emissions.',
    keyInsightsTemplate:
      'In {latestYear}, modeled light-duty emissions intensity in {locationName} was {ldvIntensity} g CO2e per mile.{intensityTrendSentence}{intensityComparisonSentence}',
    relevance:
      'Emissions per mile helps separate changes in vehicle technology and efficiency from changes in how much people drive. Total transportation emissions depend on both emissions intensity and VMT.',
    equity:
      'A fleet-average emissions rate does not describe who owns cleaner vehicles or where transportation pollution is experienced. Exposure can vary substantially near high-traffic and freight corridors.',
  },
};

export const EV_ADOPTION_CLOSEOUT: Record<string, CloseoutCopyBlock> = {
  perCapita: {
    authoringStatus: 'IMPLEMENTED_SOURCE_PARTIAL',
    overview:
      'This view normalizes registered plug-in light-duty vehicles by population, expressed per 1,000 residents. Normalization makes communities of different sizes easier to compare than raw counts, but it is not the share of households that own an EV.',
    keyInsightsTemplate:
      'In {latestYear}, {locationName} had {evRate} plug-in LDVs per 1,000 residents, representing {pluginVehicleCount} registered plug-in LDVs and {pluginShare}% of the light-duty fleet.{trendSentence}{benchmarkSentence}',
    relevance:
      'Population-normalized EV registrations help planners compare the pace of fleet electrification across places and anticipate charging and grid needs.',
    equity:
      'EVs per resident does not show who can afford an EV, who has access to home charging, or how adoption differs within the community. Use tract-level adoption, housing, income, and charging-access indicators for distributional analysis.',
  },
  projection: {
    authoringStatus: 'IMPLEMENTED_SOURCE_PARTIAL',
    overview:
      "This view extends the observed plug-in light-duty vehicle series to 2030 using the chart's documented extrapolation rule. The result is an illustrative scenario, not a forecast and not an adopted local target.",
    keyInsightsTemplate:
      'The observed {locationName} fleet reached {pluginVehicleCount} plug-in LDVs in {latestYear}. Under the displayed {projectionMethodLabel} continuation, the illustrative series reaches about {projected2030} plug-in LDVs in 2030. {projectionReferenceSentence}',
    relevance:
      'An illustrative continuation scenario can help planners test the scale of future charging and electricity needs if recent adoption patterns continue. It should not be treated as a prediction of future registrations.',
    equity:
      'A fleet-growth projection does not show which households would adopt EVs or where charging would be available. Distributional questions require separate household, housing, income, and infrastructure data.',
  },
};

export const MODE_SHARE_CLOSEOUT: CloseoutCopyBlock = {
  authoringStatus: 'READY_SOURCE_VERIFY',
  overview:
    'This chart shows how workers report traveling to work, including driving alone, carpooling, transit, walking, biking, and working from home where available. It describes the commute to work, not all daily trips. The trend lines highlight major shifts after the COVID-19 pandemic, especially in personal vehicle commuting and working from home.',
  keyInsightsTemplate:
    'In {latestYear}, {dominantMode} was the most common commute mode in {locationName}, at {dominantModeShare}%. Car, truck, or van accounted for {carShare}%, public transit {transitShare}%, walking/bicycling {activeShare}%, and working from home {wfhShare}%.{modeShareTrendSentence}',
  relevance:
    'Commute mode share can help planners understand how workers access jobs and track changes in transit, active transportation, carpooling, and remote work.',
  equity:
    'Commute mode does not cover nonwork trips or people outside the worker universe. Access to transportation options can differ by income, disability, job type, work schedule, and neighborhood; use disaggregated data when available.',
};

export const ZERO_VEHICLE_MAP_CLOSEOUT: CloseoutCopyBlock = {
  authoringStatus: 'READY_SOURCE_VERIFY',
  overview:
    'This map shows the share of households reporting no vehicles available in each census tract. It describes household vehicle availability, not the number of people who do not drive or whether a household has adequate transportation access.',
  keyInsightsTemplate:
    'Across {tractCount} tracts in the selected scope, the median tract had {medianZeroVehiclePct}% of households with no vehicle available, with tract values ranging from {minZeroVehiclePct}% to {maxZeroVehiclePct}%. {zeroVehicleContextSentence}',
  relevance:
    'Household vehicle availability can help planners identify where transit, walking, biking, shared mobility, and other nonauto options may be particularly important.',
  equity:
    'Having no vehicle can reflect preference, strong transit access, household circumstances, or financial constraint. The indicator should not be treated as a direct measure of transportation disadvantage without information about access to jobs, services, transit, disability, and household resources.',
};

export function renderCloseoutBundle(
  block: CloseoutCopyBlock,
  tokens: NarrativeTokenMap,
  equityContext?: CloseoutEquityContext
) {
  const chartId = equityContext?.chartId ?? '';
  const viewId = equityContext?.viewId ?? 'default';
  const equityV2 = equityContext
    ? resolveEquityV2(equityContext.chartId, equityContext.viewId, equityContext)
    : undefined;
  const relevanceV2 = chartId ? resolveRelevanceV2(chartId, viewId) : null;
  const bundle: AuthoredNarrativeBundle = {
    overview: block.overview,
    keyInsights: renderNarrativeTemplate(block.keyInsightsTemplate, tokens),
    relevance: block.relevance,
    relevanceV2,
    narrativeChartId: chartId || undefined,
    narrativeViewId: viewId,
    equity: equityV2 ?? block.equity,
    authoringStatus: block.authoringStatus,
    usedAuthoredCopy: true,
  };
  return bundle;
}

export function fleetNarrativeViewId(
  chartView: 'intensity' | 'by_class' | 'by_fuel',
  fuelScope: 'LDV' | 'all',
  classMode: 'vehicle_category' | 'class'
): string {
  if (chartView === 'intensity') return 'intensity';
  if (chartView === 'by_fuel') return fuelScope === 'LDV' ? 'by_fuel_ldv' : 'by_fuel_all';
  return classMode === 'vehicle_category' ? 'by_class_vehicle_category' : 'by_class_duty_class';
}
